<script setup lang="ts">
/**
 * La fiche d'une surveillance (écran 2, `rendreFiche` S:5563-5640), en lecture.
 *
 * Ce que l'utilisateur doit vivre : il lit ce qu'elle surveille, son état en
 * toutes lettres, son alarme en cours, ses seuils, si elle suit les horaires
 * du site, la consigne de son équipe, et ce qui lui est arrivé. Une donnée
 * que la réponse ne porte pas ne se dit pas « aucune » : elle se tait ou se
 * dit « — ». « Qui est prévenu » (lot F4) nomme les destinataires, l'état de
 * leur numéro et, au clic, le résultat de leurs messages de test.
 *
 * Lot F6 : les gestes de la fiche (`GestesFiche`), qui n'écrivent qu'après
 * confirmation et relisent la surveillance par `relire` ; avec une astreinte,
 * la ligne qui dit ce que le niveau donne (geste 32 : rappels jusqu'à
 * l'acquittement avec l'option Avancé, « ni rappel ni SMS » avec le Basique).
 */
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import type { Traduire } from "@modules/alarmes-v2/i18n/enregistrer"
import type { StatutHistorique } from "@modules/alarmes-v2/stores/fiche.store"
import type { StatutMembres } from "@modules/alarmes-v2/stores/membres.store"
import type {
    AlarmesV2Alarme,
    AlarmesV2Membre,
    AlarmesV2NombreDuType,
    AlarmesV2Personne,
    AlarmesV2Surveillance,
    AlarmesV2ValeurChoisie,
} from "@modules/alarmes-v2/types/alarmes-v2"
import { etatDeLaLigne, type EtatLigne } from "@modules/alarmes-v2/utils/etats"
import { nomDeLaPersonne } from "@modules/alarmes-v2/utils/journal"
import {
    formaterDate,
    formaterInstant,
    formaterMesure,
} from "@modules/alarmes-v2/utils/mesure"
import { MESSAGES_ALARMES_V2 } from "@modules/alarmes-v2/i18n/fr"
import { useAcces } from "@modules/alarmes-v2/acces/useAcces"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()
const tr = t as unknown as Traduire

// ================================
// TYPES & INTERFACES
// ================================
/** Un fait de la vie de la surveillance. */
interface Fait {
    cle: string
    quand: string
    titre: string
    texte: string
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        surveillance: AlarmesV2Surveillance
        /** Son alarme ouverte, `null` s'il n'y en a pas (lecture faite en entier). */
        alarmeOuverte?: AlarmesV2Alarme | null
        /** Ses alarmes, de la plus ancienne à la plus récente. */
        historique?: AlarmesV2Alarme[]
        statutHistorique?: StatutHistorique
        historiqueIncomplet?: boolean
        personnes?: Record<string, AlarmesV2Personne>
        /** Le nom du type d'alarme, ou `null` s'il n'est pas au catalogue. */
        nomType?: string | null
        /** Les nombres réglables du type, pour nommer les seuils. */
        nombres?: AlarmesV2NombreDuType[]
        /** Les membres lus, pour nommer les destinataires (lot F4). */
        membres?: Record<string, AlarmesV2Membre>
        statutMembres?: StatutMembres
        /** Le chemin de cette fiche. */
        cheminFiche: string
        /** Le type porte une astreinte : `true`, `false`, ou `null` (non servi). */
        astreinte?: boolean | null
        /** Relit la surveillance après une écriture ; vrai si elle a abouti. */
        relire?: () => Promise<boolean>
    }>(),
    {
        alarmeOuverte: null,
        historique: () => [],
        statutHistorique: "attente",
        historiqueIncomplet: false,
        personnes: () => ({}),
        nomType: null,
        nombres: () => [],
        membres: () => ({}),
        statutMembres: "attente",
        astreinte: null,
        relire: () => Promise.resolve(false),
    }
)

const { acces } = useAcces()

// ================================
// COMPUTED
// ================================
const ABSENT = computed(() => t("alarmesV2.mesure.absente"))

/**
 * L'alarme qui juge l'état. La surveillance dit qu'une alarme est ouverte et
 * la lecture ne la rend pas : l'état se dit « inconnu », jamais calme.
 */
const getAlarmeDeLEtat = computed((): AlarmesV2Alarme | null => {
    if (props.alarmeOuverte) return props.alarmeOuverte
    if (props.surveillance.occurrence_ouverte === true)
        return { _id: "", _id_instance: props.surveillance._id }
    return null
})

const getEtat = computed(
    (): EtatLigne =>
        etatDeLaLigne(
            props.surveillance,
            getAlarmeDeLEtat.value,
            tr,
            props.personnes
        )
)

const getInformation = computed(
    () => props.surveillance.nature === "pour_information"
)

/**
 * La ligne de l'astreinte (S:5601, geste 32) : avec l'option Avancé, les
 * rappels jusqu'à l'acquittement ; avec le Basique, « ni rappel ni SMS ».
 * Rien sans astreinte, ni quand le catalogue ne le dit pas.
 */
const getLigneAstreinte = computed((): string => {
    if (getInformation.value || props.astreinte !== true) return ""
    const a = acces("relances_astreinte")
    if (a.ok) return t("alarmesV2.actions.astreinte.avance")
    return a.affichage === "inactif"
        ? t("alarmesV2.actions.astreinte.basique")
        : ""
})

/** L'équipement et le type, sous le titre du dialogue des paramètres. */
const getContexte = computed((): string =>
    [
        props.surveillance.objet?.nom,
        props.nomType ?? t("alarmesV2.liste.typeNonDisponible"),
    ]
        .filter((x): x is string => typeof x === "string" && x !== "")
        .join(" · ")
)

/** Le sous-titre : le lieu et le type. */
const getSousTitre = computed((): string =>
    [
        props.surveillance.objet?.site_nom,
        props.nomType ?? t("alarmesV2.liste.typeNonDisponible"),
    ]
        .filter((x): x is string => typeof x === "string" && x !== "")
        .join(" · ")
)

/** « astreinte incomplète : Karim et Marie n'ont pas de numéro » (S:5128-5141). */
const getAstreinte = computed((): string => {
    const a = props.surveillance.astreinte_incomplete
    if (!a || props.surveillance.etat === "desactivee") return ""
    const ids = Array.isArray(a.personnes_sans_numero)
        ? a.personnes_sans_numero
        : []
    if (ids.length === 0) return t("alarmesV2.fiche.astreinte")
    const prenoms = ids.map(
        (id) =>
            props.personnes[id]?.prenom ||
            nomDeLaPersonne(id, props.personnes, tr)
    )
    const noms =
        prenoms.length === 1
            ? prenoms[0]
            : `${prenoms.slice(0, -1).join(", ")} et ${prenoms[prenoms.length - 1]}`
    return prenoms.length > 1
        ? t("alarmesV2.fiche.astreintePlusieurs", { noms })
        : t("alarmesV2.fiche.astreinteUne", { noms })
})

/** La dernière mesure de l'alarme en cours. */
const getMesureEnCours = computed((): string => {
    const a = props.alarmeOuverte
    if (!a || typeof a.derniere_valeur_constatee !== "number") return ""
    const quand = formaterInstant(a.derniere_valeur_le, tr)
    if (!quand) return ""
    return t("alarmesV2.fiche.derniereMesure", {
        valeur: formaterMesure(
            a.derniere_valeur_constatee,
            a.seuils_figes?.unite,
            tr
        ),
        quand,
    })
})

/** Le lien vers l'alarme en cours. */
const getLienAlarme = computed((): string =>
    props.alarmeOuverte?._id
        ? `${props.cheminFiche}/alarme/${encodeURIComponent(props.alarmeOuverte._id)}`
        : ""
)

/** Une valeur choisie, dans l'unité de son nombre. */
function valeur(v: number | null, unite: string | null | undefined): string {
    return formaterMesure(v, unite, tr)
}

/** Les paramètres nommés, et s'il en reste qu'on ne sait pas nommer. */
const getParametres = computed(() => {
    const valeurs = props.surveillance.valeurs_nombres
    if (!valeurs || typeof valeurs !== "object")
        return { lignes: [] as string[], nonAffiches: false, servis: false }
    const parCle = new Map(
        props.nombres
            .filter((n) => typeof n?.cle === "string" && n.libelle)
            .map((n) => [n.cle as string, n])
    )
    const lignes: string[] = []
    let nonAffiches = false
    for (const [cle, v] of Object.entries(valeurs) as [
        string,
        AlarmesV2ValeurChoisie,
    ][]) {
        const nombre = parCle.get(cle)
        if (!nombre) {
            nonAffiches = true
            continue
        }
        const libelle = nombre.libelle as string
        if (v === null || typeof v === "number") {
            lignes.push(
                t("alarmesV2.fiche.parametre", {
                    libelle,
                    valeur: valeur(v, nombre.unite),
                })
            )
        } else if (typeof v === "object") {
            const parEtat: string[] = []
            for (const [etat, x] of Object.entries(v)) {
                if (etat === "ouvert")
                    parEtat.push(
                        t("alarmesV2.fiche.siteOuvert", {
                            valeur: valeur(x, nombre.unite),
                        })
                    )
                else if (etat === "ferme")
                    parEtat.push(
                        t("alarmesV2.fiche.siteFerme", {
                            valeur: valeur(x, nombre.unite),
                        })
                    )
                else nonAffiches = true
            }
            if (parEtat.length > 0)
                lignes.push(
                    t("alarmesV2.fiche.parametreParEtat", {
                        libelle,
                        valeurs: parEtat.join(", "),
                    })
                )
        } else {
            // Un code (le niveau d'un écart) : sa phrase vient du type, pas lue ici.
            nonAffiches = true
        }
    }
    return { lignes, nonAffiches, servis: true }
})

/** Les horaires suivis : appliqués, refusés, ou rien de servi. */
const getHoraires = computed((): string => {
    const p = props.surveillance.profil_origine
    if (!p) return ""
    if (p.motif_refus) return t("alarmesV2.fiche.horairesNonPris")
    if (p._id_profilutilisation) return t("alarmesV2.fiche.horairesSuivis")
    return ""
})

/** La consigne : servie, servie vide, ou non servie (rien). */
const getConsigne = computed((): string | null => {
    const c = props.surveillance.consigne
    if (c === undefined) return null
    if (c === null || !c.texte) return t("alarmesV2.fiche.consigneAucune")
    const nom = nomDeLaPersonne(c.par, props.personnes, tr)
    const date = formaterDate(c.le)
    return date
        ? t("alarmesV2.fiche.consigneEcrite", { texte: c.texte, nom, date })
        : t("alarmesV2.fiche.consigneEcriteSansDate", { texte: c.texte, nom })
})

/** La vie de la surveillance : son état et depuis quand (S:5548-5561). */
const getVie = computed((): Fait[] => {
    const s = props.surveillance
    const quand = formaterInstant(s.etat_depuis, tr) ?? ABSENT.value
    const motifsDesact = MESSAGES_ALARMES_V2.motifsDesactivation as Record<
        string,
        string
    >
    if (s.etat === "armee") {
        return [
            {
                cle: "service",
                quand,
                titre: t("alarmesV2.fiche.vieMiseEnService"),
                texte: t("alarmesV2.fiche.vieMiseEnServiceTexte"),
            },
        ]
    }
    if (s.etat === "non_surveillee") {
        return [
            {
                cle: "non-surveillee",
                quand,
                titre: getEtat.value.mot,
                texte: getEtat.value.detail,
            },
        ]
    }
    if (s.etat === "desactivee") {
        const motif =
            s.desactivation_motif && s.desactivation_motif in motifsDesact
                ? t(`alarmesV2.motifsDesactivation.${s.desactivation_motif}`)
                : ""
        const nom = s.etat_par
            ? nomDeLaPersonne(s.etat_par, props.personnes, tr)
            : ""
        const texte = nom
            ? motif
                ? t("alarmesV2.fiche.vieDesactivationPar", { nom, motif })
                : t("alarmesV2.fiche.vieDesactivationParSansMotif", { nom })
            : motif
              ? t("alarmesV2.fiche.vieDesactivationMotif", { motif })
              : ""
        return [
            {
                cle: "desactivation",
                quand,
                titre: t("alarmesV2.fiche.vieDesactivation"),
                texte,
            },
        ]
    }
    return []
})
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <article class="flex flex-col gap-5" data-test="fiche">
        <!-- ================================ -->
        <!-- EN-TÊTE ET ÉTAT -->
        <!-- ================================ -->
        <header class="flex flex-col gap-2">
            <h1 class="text-xl font-semibold text-surface-900">
                {{
                    surveillance.objet?.nom ||
                    t("alarmesV2.liste.equipementSansNom")
                }}
            </h1>
            <p class="text-sm text-surface-600">{{ getSousTitre }}</p>
            <p class="flex flex-wrap items-center gap-2">
                <EtatSurveillance :etat="getEtat" />
                <span
                    class="text-sm text-surface-800"
                    data-test="etat-detail"
                    >{{ getEtat.detail }}</span
                >
            </p>
            <p v-if="getEtat.second" class="text-sm text-surface-600">
                {{ getEtat.second }}
            </p>
            <p
                v-if="getAstreinte"
                class="text-sm text-amber-800"
                data-test="astreinte"
            >
                {{ getAstreinte }}
            </p>
        </header>

        <!-- ================================ -->
        <!-- GESTES (LOT F6) -->
        <!-- ================================ -->
        <GestesFiche
            :surveillance="surveillance"
            :alarme-ouverte="alarmeOuverte"
            :personnes="personnes"
            :nombres="nombres"
            :contexte="getContexte"
            :membres="membres"
            :statut-membres="statutMembres"
            :relire="relire"
        />

        <!-- ================================ -->
        <!-- EN CE MOMENT -->
        <!-- ================================ -->
        <section class="flex flex-col gap-1" data-test="situation">
            <h2 class="text-sm font-semibold text-surface-900">
                {{ t("alarmesV2.fiche.situation") }}
            </h2>
            <template v-if="alarmeOuverte">
                <p v-if="getMesureEnCours" class="text-sm">
                    {{ getMesureEnCours }}
                </p>
                <p>
                    <RouterLink
                        :to="getLienAlarme"
                        class="text-sm font-medium text-primary-700 underline"
                        data-test="voir-alarme"
                        >{{
                            getInformation
                                ? t("alarmesV2.fiche.voirLInformation")
                                : t("alarmesV2.fiche.voirLAlarme")
                        }}</RouterLink
                    >
                </p>
            </template>
            <p
                v-else-if="surveillance.occurrence_ouverte !== true"
                class="text-sm text-surface-700"
                data-test="aucune-alarme"
            >
                {{ t("alarmesV2.fiche.aucuneAlarme") }}
            </p>
            <p class="text-sm" data-test="nature">
                <span
                    class="mr-1 rounded border px-2 py-0.5 text-xs font-medium"
                    :class="
                        getInformation
                            ? 'border-sky-200 bg-sky-50 text-sky-800'
                            : 'border-surface-300 bg-surface-50 text-surface-800'
                    "
                    >{{
                        getInformation
                            ? t("alarmesV2.fiche.information")
                            : t("alarmesV2.fiche.aTraiter")
                    }}</span
                >
                {{
                    getInformation
                        ? t("alarmesV2.fiche.informationTexte")
                        : t("alarmesV2.fiche.aTraiterTexte")
                }}
            </p>
            <p
                v-if="getLigneAstreinte"
                class="text-sm text-surface-700"
                data-test="ligne-astreinte"
            >
                {{ getLigneAstreinte }}
            </p>
        </section>

        <!-- ================================ -->
        <!-- PARAMÈTRES, HORAIRES, CONSIGNE -->
        <!-- ================================ -->
        <section class="flex flex-col gap-1" data-test="parametres">
            <h2 class="text-sm font-semibold text-surface-900">
                {{ t("alarmesV2.fiche.parametres") }}
            </h2>
            <p
                v-for="(ligne, i) in getParametres.lignes"
                :key="i"
                class="text-sm"
                data-test="parametre"
            >
                {{ ligne }}
            </p>
            <p v-if="!getParametres.servis" class="text-sm text-surface-600">
                {{ t("alarmesV2.fiche.parametresNonDisponibles") }}
            </p>
            <p
                v-else-if="getParametres.nonAffiches"
                class="text-xs text-surface-600"
                data-test="parametres-non-affiches"
            >
                {{ t("alarmesV2.fiche.parametresNonAffiches") }}
            </p>
            <div
                v-if="getHoraires"
                class="mt-2 rounded border border-surface-200 bg-surface-50 p-3 text-sm"
                data-test="horaires"
            >
                <strong class="block text-surface-900">{{
                    t("alarmesV2.fiche.horaires")
                }}</strong>
                {{ getHoraires }}
            </div>
            <p
                v-if="getConsigne !== null"
                class="mt-2 text-sm"
                data-test="consigne"
            >
                <strong>{{ t("alarmesV2.fiche.consigne") }}</strong>
                {{ getConsigne }}
            </p>
        </section>

        <!-- ================================ -->
        <!-- QUI EST PRÉVENU -->
        <!-- ================================ -->
        <QuiEstPrevenu
            :id-instance="surveillance._id"
            :destinataires="surveillance.destinataires"
            :membres="membres"
            :statut-membres="statutMembres"
            :personnes="personnes"
        />

        <!-- ================================ -->
        <!-- VIE DE LA SURVEILLANCE -->
        <!-- ================================ -->
        <section class="flex flex-col gap-2" data-test="vie">
            <h2 class="text-sm font-semibold text-surface-900">
                {{ t("alarmesV2.fiche.vie") }}
            </h2>
            <ul v-if="getVie.length > 0" class="flex flex-col gap-1">
                <li v-for="f in getVie" :key="f.cle" class="flex gap-3 text-sm">
                    <span class="w-44 shrink-0 text-surface-600">{{
                        f.quand
                    }}</span>
                    <span class="flex flex-col">
                        <span class="font-medium">{{ f.titre }}</span>
                        <span v-if="f.texte" class="text-surface-700">{{
                            f.texte
                        }}</span>
                    </span>
                </li>
            </ul>
            <HistoriqueAlarmes
                :surveillance="surveillance"
                :alarmes="historique"
                :chemin-fiche="cheminFiche"
                :statut="statutHistorique"
                :incomplet="historiqueIncomplet"
                :personnes="personnes"
            />
        </section>
    </article>
</template>
